/**
 * TrustStrip — sits just below the hero. Two STEM.org badge placeholders
 * on the left, three key numbers on the right. Stats fade in as the
 * section enters the viewport (handled here with a single IO observer).
 */
import { useEffect, useRef, useState } from 'react';

const STEM_ACCREDITED_BADGE =
  'https://res.cloudinary.com/dfa8raimq/image/upload/f_auto,q_auto/v1782230221/STM_BrandAssets_POS_BADGE_ACC-EXP_VER_kq7oiz.png';
const BEST_IN_STEM_SEAL =
  'https://res.cloudinary.com/dfa8raimq/image/upload/f_auto,q_auto/v1782230221/Best_In_STEM_Seal_b2mwnw.tiff';

export default function TrustStrip() {
  const sectionRef = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || revealed) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setRevealed(true),
      { threshold: 0.2 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [revealed]);

  return (
    <section
      ref={sectionRef}
      className="bg-rsc-slate py-10 sm:py-12 border-b border-rsc-card-border font-poppins"
      data-testid="trust-strip"
    >
      <div className="container-app grid md:grid-cols-2 gap-8 items-center">
        {/* Badges */}
        <div className="flex items-center gap-5 sm:gap-6 justify-center md:justify-start">
          <Badge
            src={BEST_IN_STEM_SEAL}
            title="Best In STEM"
            subtitle="STEM.org award"
          />
          <Badge
            src={STEM_ACCREDITED_BADGE}
            title="Accredited Experience"
            subtitle="STEM.org trustmark"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Stat n="12,000+" l="Learners served" revealed={revealed} />
          <Stat n="28" l="School partners" revealed={revealed} />
          <Stat n="Top 5%" l="Of STEM programs nationally" revealed={revealed} />
        </div>
      </div>
    </section>
  );
}

function Badge({ src, title, subtitle }) {
  return (
    <div className="flex items-center gap-3">
      <img
        src={src}
        alt={title}
        loading="lazy"
        className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
      />
      <div>
        <div className="text-sm font-extrabold text-rsc-brand-blue leading-tight">{title}</div>
        <div className="text-xs text-rsc-charcoal-muted">{subtitle}</div>
      </div>
    </div>
  );
}

function Stat({ n, l, revealed }) {
  return (
    <div
      className={`text-center transition-all duration-700 ${
        revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
      }`}
    >
      <div className="text-2xl sm:text-3xl font-extrabold text-rsc-brand-blue leading-none">{n}</div>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-rsc-charcoal-muted mt-1.5 leading-tight">
        {l}
      </div>
    </div>
  );
}
